import React, { useState, useRef, useEffect } from 'react';
import { flushSync } from 'react-dom';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Mail, Phone, MessageSquare, UserPlus,
  Heart, Scale, GraduationCap, Leaf, Globe, Home, Users,
  Trophy, Briefcase, TrendingUp, Film, BookOpen, Cpu, Building2, ScrollText,
  Layers,
  Play, Pause, RotateCcw, RotateCw, ArrowLeft, Volume2, X
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ORB_LOGO = "https://customer-assets.emergentagent.com/job_4f2b8787-9e39-421b-9343-dec62de2fe7e/artifacts/jrh9uqnh_Untitled%20design%20%287%29.png";
const PITCH_VIDEO = "https://customer-assets.emergentagent.com/job_fb180cb2-6be0-4004-8c69-b9e6acdca6ae/artifacts/6l5vvgs0_3m%20Pitch%20%285%29.mp4";

// iter175cb — These hardcoded CAP arrays now only serve as the
// initial render-before-fetch fallback. The CHOOSE-YOUR-CAUSE popup
// reads its 15 cards from `/api/business-cards/public-settings`
// (driven by the admin's "Caps" tab in the Business Card Management
// page). Whenever the server returns 15 valid caps we replace these
// in-memory.  Keep them in sync with `_default_caps()` in
// /app/backend/business_cards.py so the very first render of the
// card on a brand-new install isn't blank.
//
// Icons stay in the frontend — admins don't manage Lucide imports.
// `resolveCapIcon` maps a title → icon component. Any title the
// admin types that isn't in the map falls back to the generic
// `Layers` icon, so renaming a cap never breaks the card.

const DEFAULT_CAP_CATEGORIES = [
  { label: 'Health', icon: Heart },
  { label: 'Criminal Justice', icon: Scale },
  { label: 'Education', icon: GraduationCap },
  { label: 'Environment', icon: Leaf },
  { label: 'International Relations', icon: Globe },
  { label: 'Housing', icon: Home },
  { label: 'Gender Issues', icon: Users },
  { label: 'Sports', icon: Trophy },
  { label: 'Labor & Employment', icon: Briefcase },
  { label: 'Economics', icon: TrendingUp },
  { label: 'Media & Culture', icon: Film },
  { label: 'Religion & Faith', icon: BookOpen },
  { label: 'Emerging Technologies', icon: Cpu },
  { label: 'Public Affairs', icon: Building2 },
  { label: 'The Constitution', icon: ScrollText }
];

// iter175cb — icon resolver. Try exact case-insensitive match first;
// then a few aliases the admin might naturally type; otherwise the
// generic Layers icon.
const CAP_ICON_ALIASES = {
  'economy & wealth': 'Economics',
  'economy': 'Economics',
  'technology & privacy': 'Emerging Technologies',
  'technology': 'Emerging Technologies',
  'politics & governance': 'Public Affairs',
  'politics': 'Public Affairs',
  'animal welfare': 'Environment',
};
const ICON_BY_NAME = {
  'Health': Heart, 'Criminal Justice': Scale, 'Education': GraduationCap,
  'Environment': Leaf, 'International Relations': Globe, 'Housing': Home,
  'Gender Issues': Users, 'Sports': Trophy, 'Labor & Employment': Briefcase,
  'Economics': TrendingUp, 'Media & Culture': Film, 'Religion & Faith': BookOpen,
  'Emerging Technologies': Cpu, 'Public Affairs': Building2,
  'The Constitution': ScrollText,
};
const resolveCapIcon = (title) => {
  const norm = (title || '').trim();
  if (ICON_BY_NAME[norm]) return ICON_BY_NAME[norm];
  const lower = norm.toLowerCase();
  for (const k of Object.keys(ICON_BY_NAME)) {
    if (k.toLowerCase() === lower) return ICON_BY_NAME[k];
  }
  const aliased = CAP_ICON_ALIASES[lower];
  if (aliased && ICON_BY_NAME[aliased]) return ICON_BY_NAME[aliased];
  return Layers;
};

// iter175cb — `CAP_CATEGORIES` const removed; the component now
// uses the `capCategories` state which defaults to
// `DEFAULT_CAP_CATEGORIES` and replaces itself with the admin-saved
// list on every load of /api/business-cards/public-settings.

const DEFAULT_CAP_CONTENT = {
  'Media & Culture': {
    body: `Collective Action Problems ("CAPs") show up across media and culture in a variety of ways—misinformation, censorship, online harassment, echo chambers, clickbait, and algorithm-driven content, among others.\n\nAt their core, these issues stem from a gap between what benefits the group and what benefits the powerful individual. Without systems that better align those incentives, powerful individual interests often end up shaping outcomes.`,
    example: {
      header: 'Internet Censorship',
      body: `Group Interest: Society benefits from an open marketplace of ideas. This supports accountability, encourages critical thinking, promotes social progress, and helps create a more informed and participatory public.\n\nIndividual Interest: Various actors—such as political groups, media organizations, and others—may have incentives to shape or limit the flow of information in ways that serve their goals. This can sometimes result in content being filtered, moderated, or deprioritized, especially when it challenges existing narratives or structures.\n\nThis is where ORB comes in—giving The People the tools to align incentives, coordinate action, and make a real-world impact.`
    }
  },
  'Religion & Faith': {
    body: `Collective action problems show up in religion and faith in a variety of ways—interfaith dialogue, religious discrimination, extremism and violence, child marriage, poverty and inequality, and persecution, among others.\n\nThese challenges often stem from a gap between what benefits society as a whole and what powerful individual actors prioritize. Without systems that better align those incentives, progress can be limited.`,
    example: {
      header: 'Religious-Based Poverty & Inequality',
      body: `Societal Interest: Society benefits from more effective, long-term approaches to reducing poverty and inequality. Many religious organizations play a vital role in supporting communities through charitable services like food, shelter, and healthcare.\n\nPowerful Individual Interest: In many cases, efforts are focused on addressing immediate needs rather than the underlying causes of poverty or structural inequality. Broader, systemic solutions can be harder to pursue due to differing beliefs, institutional priorities, or concerns about disrupting existing structures.\n\nThis is where ORB comes in—giving The People the tools to align incentives, coordinate action, and make a real-world impact.`
    }
  },
  'Health': {
    body: `Collective action problems show up across the health industry in many ways—access to care, quality of treatment, insurance systems, fitness, and nutrition, among others.\n\nThese challenges stem from a gap between what society needs and what powerful individual actors prioritize. Without systems that better align those incentives, outcomes tend to reflect those imbalances.`,
    example: {
      header: 'Healthcare Insurance',
      body: `Societal Interest: Society depends on health insurance systems that reliably cover necessary care—ensuring people can access treatment for serious and life-altering conditions when they need it most.\n\nPowerful Individual Interest: Insurance providers operate under strong financial incentives to maximize profitability. In practice, this can result in denied claims, administrative barriers, and delays that make accessing care more difficult—especially at critical moments. These dynamics are not accidental; they are reinforced by sophisticated industry lobbying and regulatory influence that shape the rules in ways that preserve these incentives.\n\nThis is where ORB comes in—giving The People the tools to align incentives, coordinate action, and make a real-world impact.`
    }
  },
  'Criminal Justice': {
    body: `Collective action problems show up across the criminal justice system in many ways—mass incarceration, private prisons, racial disparities, public defender funding, sentencing, trial delays, police accountability, recidivism, rehabilitation, and mental health, among others.\n\nThese challenges stem from a gap between what society needs and what powerful individual actors prioritize. Without systems that better align those incentives, the imbalance persists.`,
    example: {
      header: 'Public Defender Funding',
      body: `Societal Interest: The Sixth Amendment guarantees the right to adequate legal representation. For that promise to hold, public defender systems must be properly funded and resourced. Instead, they are often underfunded, understaffed, and burdened with caseloads that create clear disparities in outcomes.\n\nPowerful Individual Interest: Public defense funding is frequently deprioritized within broader "tough on crime" political incentives, where investing in defense carries less political upside than funding enforcement. As a result, public defenders operate under severe constraints, with limited capacity to challenge the system effectively. These conditions are sustained by political pressures and lobbying forces that favor maintaining existing allocations rather than rebalancing resources toward fairness.\n\nThis is where ORB comes in—giving The People the tools to align incentives, coordinate action, and make a real-world impact.`
    }
  },
  'Education': {
    body: `Collective action problems show up across education in many ways—school funding, teacher pay, retention, access to higher education, district inequality, inclusion for students with disabilities, school choice, wealth-based achievement gaps, and student mental health, among others.\n\nThese challenges stem from a gap between what society needs and what powerful individual actors prioritize. Without systems that better align those incentives, the disparities persist.`,
    example: {
      header: 'Teacher Pay',
      body: `Societal Interest: Society depends on well-supported, high-quality educators to teach and develop the next generation. That requires consistent investment, fair compensation, and conditions that attract and retain strong teachers across all communities.\n\nPowerful Individual Interest: In many regions, school funding is tied to local property taxes—an approach that predictably advantages wealthier areas while under-resourcing poorer ones. The result is a system where compensation, support, and educational quality often track neighborhood wealth. Despite the visibility of these disparities, meaningful reform has been slow, shaped by political incentives and entrenched interests that benefit from maintaining the current structure.\n\nThis is where ORB comes in—giving The People the tools to align incentives, coordinate action, and make a real-world impact.`
    }
  },
  'Environment': {
    body: `Collective action problems show up across the environment in many ways—climate change, air and water pollution, plastic waste, deforestation, ocean acidification, energy use, land use, natural resource management, and disaster response, among others.\n\nThese challenges stem from a gap between what society needs and what powerful individual actors prioritize. Without systems that better align those incentives, the risks compound over time.`,
    example: {
      header: 'Natural Disasters (Preparedness)',
      body: `Societal Interest: Society depends on strong, coordinated preparation for natural disasters—ensuring that infrastructure, emergency services, and insurance systems are equipped to mitigate harm, save lives, and reduce economic loss when events like hurricanes, floods, wildfires, and heat waves occur.\n\nPowerful Individual Interest: Disaster preparedness often competes with short-term political and financial incentives. Investments in prevention and resilience are frequently delayed or underfunded, while regulatory and budgetary decisions prioritize nearer-term considerations. At the same time, insurance providers—operating under pressure to manage risk exposure—may scale back or withdraw coverage in high-risk areas ahead of disasters. These dynamics, reinforced by industry influence and policy tradeoffs, leave communities more vulnerable when disasters inevitably strike.\n\nThis is where ORB comes in—giving The People the tools to align incentives, coordinate action, and make a real-world impact.`
    }
  },
  'International Relations': {
    body: `Collective action problems show up across international relations in many ways—immigration, crime, human rights, arms control, conflict, energy and environmental policy, global health, and trade, among others.\n\nThese challenges stem from a gap between what society needs at a global level and what powerful individual actors prioritize. Without systems that better align those incentives, coordination breaks down and shared problems persist.`,
    example: {
      header: 'Immigration (Refugee Resettlement)',
      body: `Societal Interest: There is a clear global interest in providing refuge for people fleeing war, persecution, and disaster—both as a humanitarian obligation and as a recognition that instability, if left unaddressed, carries broader consequences for everyone.\n\nPowerful Individual Interest: National leaders often operate within domestic political constraints that discourage large-scale resettlement efforts. Concerns around economic pressure, social integration, labor market impacts, and political backlash shape decision-making. As a result, responsibility is frequently deferred or unevenly distributed, despite the scale and urgency of the need. These patterns are reinforced by political incentives that prioritize short-term stability at home over coordinated global solutions.\n\nThis is where ORB comes in—giving The People the tools to align incentives, coordinate action, and make a real-world impact.`
    }
  },
  'Housing': {
    body: `Collective action problems show up across housing in many ways—affordability, quality, availability, discrimination, displacement, overcrowding, and homelessness, among others.\n\nThese challenges stem from a gap between what society needs and what powerful individual actors prioritize. Without systems that better align those incentives, these imbalances persist.`,
    example: {
      header: 'Housing Availability',
      body: `Societal Interest: Society depends on widespread access to stable, affordable housing. When people are securely housed, communities are safer, healthier, and more economically resilient.\n\nPowerful Individual Interest: Real estate development is often driven by returns, not need. Higher-end projects typically generate stronger margins, which incentivizes building luxury units over more affordable alternatives—even in markets where demand for lower-cost housing is far greater. At the same time, zoning constraints, local opposition, and policy incentives often reinforce this pattern. The result is a persistent underproduction of housing that meets broader societal needs.\n\nThis is where ORB comes in—giving The People the tools to align incentives, coordinate action, and make a real-world impact.`
    }
  },
  'Gender Issues': {
    body: `Collective action problems show up across gender issues in many ways—pay equity, workplace harassment, discrimination, access to reproductive care, medical transitions, and representation, among others.\n\nThese challenges stem from a gap between what society needs and what powerful individual actors prioritize. Without systems that better align those incentives, progress is often uneven and slow.`,
    example: {
      header: 'Gender Representation in Workplace Leadership',
      body: `Societal Interest: Society benefits from diverse leadership. A broader range of perspectives strengthens decision-making, drives innovation, and improves overall organizational performance.\n\nPowerful Individual Interest: Leadership pipelines are often shaped by existing power structures and ingrained biases—both conscious and unconscious. Hiring and promotion decisions tend to favor familiarity and continuity, rather than actively prioritizing diversity. At the same time, there is often an assumption that these imbalances will correct themselves over time, reducing the urgency for deliberate action. These dynamics allow disparities in representation to persist, even when the broader benefits of change are well understood.\n\nThis is where ORB comes in—giving The People the tools to align incentives, coordinate action, and make a real-world impact.`
    }
  },
  'Sports': {
    body: `Collective action problems show up across the sports industry in many ways—match-fixing, financial misconduct, performance-enhancing drugs, tanking, youth specialization, fair pay, working conditions, diversity in coaching, and athlete health risks, among others.\n\nThese challenges stem from a gap between what society needs and what powerful individual actors prioritize. Without systems that better align those incentives, these issues persist beneath the surface of the industry.`,
    example: {
      header: 'Health Risks (Concussions & Traumatic Brain Injuries)',
      body: `Societal Interest: Society depends on sports and extracurriculars that are not only engaging, but fundamentally safe for those who participate in them—especially when long-term health is at stake.\n\nPowerful Individual Interest: Sports organizations have strong incentives to protect the image and profitability of their leagues, which can lead to minimizing or downplaying the long-term risks of concussions and brain injuries. At the same time, regulatory action is often slow or limited, shaped by political caution and pressure from well-organized industry stakeholders. The result is a system where known risks persist, while accountability and reform lag behind the evidence.\n\nThis is where ORB comes in—giving The People the tools to align incentives, coordinate action, and make a real-world impact.`
    }
  },
  'Labor & Employment': {
    body: `Collective action problems show up across labor and employment in many ways—minimum wage, paid family leave, vacation time and pay, remote work, employment classification, benefits, discrimination, retaliation, and wrongful termination, among others.\n\nThese challenges stem from a gap between what society needs and what powerful individual actors prioritize. Without systems that better align those incentives, these tensions persist.`,
    example: {
      header: 'Vacation Time & Pay',
      body: `Societal Interest: Society depends on a healthy, sustainable workforce—where people have the time and capacity to rest, care for their families, and remain fully present and productive in their work. Adequate paid time off is a core part of that balance.\n\nPowerful Individual Interest: Workplace policies are often shaped by short-term financial priorities. Expanding paid time off can be viewed as a direct cost, even when it contributes to long-term productivity, retention, and stability. As a result, many organizations limit or resist broader vacation policies, reinforcing a culture where burnout is normalized and recovery is deprioritized.\n\nThis is where ORB comes in—giving The People the tools to align incentives, coordinate action, and make a real-world impact.`
    }
  },
  'Economics': {
    body: `Collective action problems show up across economics in many ways—multinational tax avoidance, wealth inequality, public debt, and the long-term sustainability of social systems, among others.\n\nThese challenges stem from a gap between what society needs and what powerful individual actors prioritize. Without systems that better align those incentives, these imbalances persist.`,
    example: {
      header: 'Multinational Corporation Global Tax Avoidance',
      body: `Societal Interest: Society depends on a fair and functional tax system—one where contributions are broadly shared and public resources can be sustained to support infrastructure, services, and long-term economic stability.\n\nPowerful Individual Interest: Multinational Corporations simply want to maximize their profits. To do so, they avoid paying taxes by utilizing highly complex strategies such as storing funds in offshore accounts and shifting their Intellectual property and other profits to low tax jurisdictions. These approaches are often supported by sophisticated legal frameworks and reinforced by lobbying efforts that shape international tax rules, allowing these practices to persist at scale.\n\nThis is where ORB comes in—giving The People the tools to align incentives, coordinate action, and make a real-world impact.`
    }
  },
  'Emerging Technologies': {
    body: `Collective action problems show up across emerging technologies in many ways—AI development standards, cybersecurity coordination, data privacy, gene editing ethics, blockchain governance, autonomous systems, electronic waste, and drone regulation, among others.\n\nThese challenges stem from a gap between what society needs and what powerful individual actors prioritize. Without systems that better align those incentives, innovation can outpace responsibility.`,
    example: {
      header: 'Electronic Waste Disposal',
      body: `Societal Interest: Society depends on technological progress that does not come at the expense of environmental and public health. That includes minimizing toxic waste and preventing harmful materials—like lead, mercury, and cadmium—from entering ecosystems.\n\nPowerful Individual Interest: Electronic manufacturers only care about increasing profits. In the tech world, that means making products with shorter life spans so that you can sell more. By designing for shorter lifespans, limited repairability, and quick obsolescence, a company can drive repeat purchases and growth their revenue substantially. At scale, these incentives contribute directly to rising volumes of electronic waste. Efforts to combat the issue often face resistance from industry stakeholders seeking to preserve existing margins and control over product ecosystems.\n\nThis is where ORB comes in—giving The People the tools to align incentives, coordinate action, and make a real-world impact.`
    }
  },
  'Public Affairs': {
    body: `Collective action problems show up across public affairs in many ways—lobbying, campaign finance, public health initiatives, transportation, public administration, and social services, among others.\n\nThese challenges stem from a gap between what society needs and what powerful individual actors prioritize. Without systems that better align those incentives, public outcomes often reflect those imbalances.`,
    example: {
      header: 'Campaign Finance',
      body: `Societal Interest: Society depends on democratic processes that are fair, representative, and responsive to the broader public. That requires limiting the outsized influence of money in politics so that elected officials are accountable to the people they serve.\n\nPowerful Individual Interest: Political candidates and their parties rely heavily on large donations from wealthy individuals, big corporations, and powerful interest groups. Because of the Campaign finance laws, politicians—on all sides—often disavow their allegiance to The People, and become puppets to the Elites and their massive donations. The law allows these dynamics to persist, making it incredibly difficult to rebalance political power for the benefit of The People.\n\nThis is where ORB comes in—giving The People the tools to align incentives, coordinate action, and make a real-world impact.`
    }
  },
  'The Constitution': {
    body: `Collective action problems show up in how the Constitution is interpreted, applied, and updated—through amendments, voter participation, electoral reform, federal regulation, policy development, and the judiciary, among others.\n\nThese challenges stem from a gap between what society needs and what powerful individual actors prioritize. Without systems that better align those incentives, meaningful change is routinely blocked.`,
    example: {
      header: 'Constitutional Amendments',
      body: `Societal Interest: Society needs a Constitution that not only defines how the country functions, but can evolve when it no longer serves the people effectively. A functioning amendment process is essential to keeping the system legitimate and responsive.\n\nPowerful Individual Interest: Political actors stall and or kill amendments not because they lack merit, but because they threaten power. If a reform risks upsetting major donors, weakening party control, or giving an advantage to political opponents, it simply doesn't move—even when it benefits a vast majority of The People. Ultra-wealthy donors and entrenched interest groups don't just influence the process; they decide what's even allowed to be considered. The result is a system where necessary change is obvious, widely discussed, and consistently blocked.\n\nThis is where ORB comes in—giving The People the tools to align incentives, coordinate action, and make a real-world impact.`
    }
  }
};

const socialLinks = [
  { name: 'Facebook', icon: 'fab fa-facebook-f', url: 'https://www.facebook.com/collectiveorb' },
  { name: 'Instagram', icon: 'fab fa-instagram', url: 'https://www.instagram.com/collectiveorb/' },
  { name: 'LinkedIn', icon: 'fab fa-linkedin-in', url: 'https://www.linkedin.com/company/collectiveorb' },
  { name: 'X', icon: 'fab fa-x-twitter', url: 'https://x.com/collectiveorb' },
  { name: 'TikTok', icon: 'fab fa-tiktok', url: 'https://www.tiktok.com/@collectiveorb' }
];

const ORB_PHONE = '555-555-5555';

const BusinessCardContent = React.forwardRef(({ cardData, initialPopup = null, isVisible = true, compact = false, extraBottomPx = 0 }, externalRef) => {
  const [activePopup, setActivePopup] = useState(initialPopup);
  // Pitch settings — fetched once from /api/business-cards/public-settings.
  // Determines whether "Watch the Pitch" plays, shows a coming-soon
  // overlay, or is hidden entirely.
  const [pitchState, setPitchState] = useState('normal');
  // Track whether the public-settings fetch has completed at least
  // once. Until then we MUST NOT auto-open the pitch popup — defaulting
  // to 'normal' before the fetch resolves is exactly what was causing
  // the pitch to play even after the admin had disabled it. Treated
  // strictly as a permission gate: pitch is allowed ONLY when state
  // is positively confirmed as 'normal'; any other state (or unknown)
  // routes to the CAPs popup.
  const [pitchStateLoaded, setPitchStateLoaded] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState('');
  // iter175cb — Admin-managed CAPs payload. Defaults to the hardcoded
  // fallback so the popup renders before/if the public-settings fetch
  // never resolves; replaced by whatever the admin saved as soon as
  // the fetch returns. The fallback is sourced from
  // /app/backend/business_cards.py::_default_caps() to match what a
  // brand-new install would render.
  const [capCategories, setCapCategories] = useState(DEFAULT_CAP_CATEGORIES);
  const [capContent, setCapContent] = useState(DEFAULT_CAP_CONTENT);
  // Fired when pitchState==='inactive' and the user taps the pitch
  // button — shows the coming-soon overlay for 5s then redirects to
  // the waitlist popup. The user can dismiss earlier with the X.
  const [comingSoonRedirectId, setComingSoonRedirectId] = useState(null);

  // ── Public-card analytics ──────────────────────────────────
  // Per-tab session id — sessionStorage survives in-tab nav but
  // dies when the tab closes, which matches the spec ("only lump
  // activity for the same session"). We mint per (card_user_id)
  // so the same browser session viewing multiple cards is counted
  // separately.
  const sessionIdRef = useRef('');
  const cardId = cardData?.id || cardData?.user_id;
  if (cardId && !sessionIdRef.current) {
    try {
      const k = `bc_sess_${cardId}`;
      let sid = sessionStorage.getItem(k);
      if (!sid) {
        sid = (crypto.randomUUID && crypto.randomUUID()) || `s_${Date.now()}_${Math.random().toString(36).slice(2)}`;
        sessionStorage.setItem(k, sid);
      }
      sessionIdRef.current = sid;
    } catch (_e) {
      sessionIdRef.current = `s_${Date.now()}`;
    }
  }
  const sourceRef = useRef('unknown');
  if (typeof window !== 'undefined' && sourceRef.current === 'unknown') {
    try {
      const params = new URLSearchParams(window.location.search);
      const raw = (params.get('src') || '').toLowerCase();
      // Accept canonical names AND legacy aliases (email_sig/sms/qr)
      // so that older shared links still attribute correctly.
      const ALIASES = { email_sig: 'email_signature', sms: 'text_message', qr: 'qr_code_scan' };
      const s = ALIASES[raw] || raw;
      if (['email_signature', 'orb_text', 'copy_link', 'text_message', 'qr_code_scan'].includes(s)) {
        sourceRef.current = s;
      }
    } catch (_e) {/* noop */}
  }
  const track = React.useCallback((event_type, extra = {}) => {
    if (!cardId) return;
    try {
      const apiBase = process.env.REACT_APP_BACKEND_URL || '';
      const body = {
        card_user_id: cardId,
        event_type,
        session_id: sessionIdRef.current,
        source: sourceRef.current,
        ...extra,
      };
      // Fire-and-forget — never block UI on tracking calls. Use
      // `keepalive` so the request survives a page-unload during
      // tab close.
      fetch(`${apiBase}/api/card-track`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        keepalive: true,
      }).catch(() => {});
    } catch (_e) {/* noop */}
  }, [cardId]);
  // Single 'view' event when the card mounts. iter175cg — enrich
  // with document.referrer + IANA timezone so the backend can
  // surface origin platform and visitor TZ in the session card.
  // iter175ch — Guard against React StrictMode / dev double-invoke
  // and re-renders that change `track`'s identity. We fire exactly
  // once per cardId per mount.
  const viewTrackedRef = useRef(null);
  useEffect(() => {
    if (!cardId) return;
    if (viewTrackedRef.current === cardId) return;
    viewTrackedRef.current = cardId;
    let referrer = '';
    let tz = '';
    try { referrer = (document.referrer || '').slice(0, 2048); } catch (_e) {/**/}
    try { tz = Intl.DateTimeFormat().resolvedOptions().timeZone || ''; } catch (_e) {/**/}
    track('view', { referrer, tz });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardId]);
  // Tracks whether the pitch was auto-opened via ?pitch=1 or
  // initialPopup="pitch" so we can autoplay the video once it mounts.
  const [autoplayPitch, setAutoplayPitch] = useState(initialPopup === 'pitch');
  // Query-param driven auto-opens. Emails sent via Uplift include a
  // "Watch the ORB Pitch" button linking to /card/:id?pitch=1 — we
  // pop the pitch modal immediately when that param is present so
  // recipients land straight in the video.
  // Query-param driven auto-opens. Emails sent via Uplift include a
  // "Watch the ORB Pitch" button linking to /card/:id?pitch=1 — we
  // pop the pitch modal immediately when that param is present so
  // recipients land straight in the video.
  //
  // PERMISSION GATE: ?pitch=1 auto-arrival is still gated by the
  // current admin pitch_state. If the admin disabled pitch AFTER the
  // email was sent, we MUST NOT autoplay the video — fall through to
  // the CAPs popup instead. The check below defers until the public
  // settings have loaded; until then, we open neither (preventing a
  // policy-violating flash of pitch content).
  useEffect(() => {
    if (!pitchStateLoaded) return;
    try {
      const params = new URLSearchParams(window.location.search);
      const wantsPitch = params.get('pitch') === '1';
      const wantsCaps = params.get('caps') === '1';
      if (wantsPitch && !activePopup) {
        if (pitchState === 'inactive' || pitchState === 'disabled') {
          // Stale link from before the admin disabled pitch — route
          // to CAPs instead of honoring the legacy ?pitch=1 param.
          setActivePopup('caps');
        } else {
          setActivePopup('pitch');
          setAutoplayPitch(true);
        }
      } else if (wantsCaps && !activePopup) {
        // Email pill redirects here when pitch_state is inactive or
        // disabled — open the CAPs popup directly.
        setActivePopup('caps');
      }
    } catch (_e) {/* SSR / old browser — ignore */}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pitchStateLoaded]);

  // Fetch public business-card settings once on mount. Drives the
  // pitch-button behavior (normal / inactive / disabled) and the
  // Download ORB target URL.
  //
  // PERMISSION GATE: `pitch_state` is treated as an admin permission
  // (see PitchEditor in BusinessCardManagementPage). The "Learn about
  // ORB" pill in email signatures, the in-app email preview, and the
  // public card view all MUST defer to whatever the admin most-recently
  // set. The `pitchStateLoaded` flag below makes that wiring explicit:
  // until the fetch confirms a value, every entry point that *could*
  // open the pitch popup will route to the CAPs popup instead.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const apiBase = process.env.REACT_APP_BACKEND_URL || '';
        const r = await fetch(`${apiBase}/api/business-cards/public-settings`);
        if (!r.ok) return;
        const j = await r.json();
        if (cancelled) return;
        setPitchState(j.pitch_state || 'normal');
        setDownloadUrl(j.download_url || '');
        // iter175cb — Caps. Admin-managed list (Business Card Mgmt →
        // Caps tab). Replace the hardcoded defaults with the server
        // payload whenever it's available. Icons are still resolved
        // client-side via `resolveCapIcon(title)`.
        const adminCaps = Array.isArray(j.caps) ? j.caps : [];
        if (adminCaps.length >= 1) {
          const categories = adminCaps.map((c) => ({
            label: (c.title || '').trim() || 'Untitled',
            icon: resolveCapIcon(c.title),
          }));
          const content = {};
          for (const c of adminCaps) {
            const label = (c.title || '').trim() || 'Untitled';
            content[label] = {
              body: c.body || '',
              example: {
                header: c.example_header || '',
                body: c.example_body || '',
              },
            };
          }
          setCapCategories(categories);
          setCapContent(content);
        }
      } catch (_e) {/* network — keep defaults */}
      finally {
        if (!cancelled) setPitchStateLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);
  const [contactAction, setContactAction] = useState(null);
  const [contactForm, setContactForm] = useState({ first_name: '', last_name: '', email: '', phone: '', notes: '', sms_consent: false });
  // iter175bl — Session-scoped bypass. Once the visitor has filled in
  // the contact gate once during THIS render of the business-card
  // page, every subsequent action button (email / call / text / save)
  // skips the form popup and fires straight through. The bypass lives
  // ONLY in React state — refreshing or navigating away unmounts the
  // component and the gate slams shut again.
  const [capturedContact, setCapturedContact] = useState(null);
  const [waitlistForm, setWaitlistForm] = useState({
    first_name: '', last_name: '', email: '', phone: '',
    address: '', social_handles: '',
    enter_drawing: false, opt_in_contact: false
  });
  const [selectedCap, setSelectedCap] = useState(null);
  // iter175cg — Track wall-clock time spent on each CAP detail
  // view. When `selectedCap` transitions away (back to grid, popup
  // closed, or another CAP picked) we emit a `cap_time` event with
  // `view_ms` = ms spent on the previous CAP. Stored in refs so the
  // effect can read both "what was open" and "when did we open it"
  // without re-triggering itself.
  const prevCapRef = useRef(null);
  const capOpenedAtRef = useRef(0);
  useEffect(() => {
    const prev = prevCapRef.current;
    if (prev && prev !== selectedCap) {
      const ms = Date.now() - (capOpenedAtRef.current || Date.now());
      if (ms > 0 && ms < 60 * 60 * 1000) {
        try { track('cap_time', { cap_title: prev, cap_id: prev, view_ms: ms }); } catch (_) {}
      }
    }
    prevCapRef.current = selectedCap;
    capOpenedAtRef.current = selectedCap ? Date.now() : 0;
  }, [selectedCap, track]);
  const [typedCap, setTypedCap] = useState('');
  const [typedBody, setTypedBody] = useState('');
  const [showExampleBtn, setShowExampleBtn] = useState(false);
  const [exDissolving, setExDissolving] = useState(false);
  const [exampleActive, setExampleActive] = useState(false);
  const [typedExHeader, setTypedExHeader] = useState('');
  const [typedExBody, setTypedExBody] = useState('');
  const [showExCTAs, setShowExCTAs] = useState(false);
  const [skipTyping, setSkipTyping] = useState(false);
  // Skip button is hidden for the first 3 seconds after a CAP body
  // (or its Example) starts typing — prevents it from popping in
  // immediately and feeling premature.
  const [showSkipBtn, setShowSkipBtn] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const videoRef = useRef(null);
  // Track whether we already fired a 'pitch_played' analytics
  // event for this view — prevents spam when the user pauses and
  // resumes (we only care about the first start).
  const pitchPlayedTrackedRef = useRef(false);
  // Last % we reported in 'pitch_progress' so we can throttle to
  // ~every 10%.
  const lastPitchPctRef = useRef(0);
  const profilePicRef = useRef(null);
  const [pitchTopPx, setPitchTopPx] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [showControls, setShowControls] = useState(false);
  // Tracks whether the <video> element is currently muted. We mirror
  // the actual element's `muted` property via the `volumechange`
  // event so we can render a "Tap for sound" overlay any time the
  // browser's autoplay policy has forced us into muted playback.
  const [videoMuted, setVideoMuted] = useState(true);
  // Once the popup was opened via a synchronous user gesture
  // (direct "Watch the Pitch" click or in-app email pill), we
  // suppress the "Tap for sound" overlay for the lifetime of that
  // view — the user already engaged so the hint is unnecessary
  // even if the browser silently fell back to muted playback.
  // The overlay only re-arms on URL-driven auto-arrival (?pitch=1).
  const [suppressTapOverlay, setSuppressTapOverlay] = useState(false);
  const [gradientOn, setGradientOn] = useState(false);
  const hideControlsTimer = useRef(null);
  // Set to true by `startPitch` / `playPitch` when the pitch popup
  // is opened by a synchronous user gesture (the in-card "Watch the
  // Pitch" button or the in-app email preview). In that path, the
  // browser allows unmuted autoplay so the "Tap for sound" overlay
  // is unnecessary. Set to false on auto-arrival paths (e.g. ?pitch=1
  // from the email link) where the browser still blocks autoplay with
  // sound — there we DO want the overlay so the user can unmute.
  const gestureUnmuteRef = useRef(false);

  // Single source of truth for "open the pitch and start the video
  // RIGHT NOW inside the user gesture". Used by both the in-card
  // "Watch the Pitch" button and the "Watch the ORB Pitch" hero
  // button. Synchronous v.play() is required so Safari/Chrome let the
  // audio play unmuted; setTimeouts lose the gesture token.
  const startPitch = () => {
    // Force the pitch popup (and its <video>) to render SYNCHRONOUSLY
    // inside the click handler so we can call v.play() while still
    // inside the user-gesture call stack — required for unmuted
    // autoplay on Chrome/Safari/iOS.
    gestureUnmuteRef.current = true;
    flushSync(() => {
      setActivePopup('pitch');
      setAutoplayPitch(true);
    });
    const v = videoRef.current;
    if (!v) return;
    v.muted = false;
    try { if (v.networkState === 3 || v.readyState === 0) v.load(); } catch (_e) {/* noop */}
    try { v.currentTime = 0; } catch (_e) {/* noop */}
    const p = v.play();
    if (p && typeof p.catch === 'function') {
      p.catch(() => {
        v.muted = true;
        const p2 = v.play();
        if (p2 && typeof p2.catch === 'function') p2.catch(() => {});
      });
    }
  };

  const revealControls = () => {
    setShowControls(true);
    if (hideControlsTimer.current) clearTimeout(hideControlsTimer.current);
    hideControlsTimer.current = setTimeout(() => setShowControls(false), 2500);
  };

  // Imperative handle so parent components (e.g. EmailPage's in-app
  // ORB preview) can play the pitch video INSIDE the user-gesture
  // call stack, before React commits the open-state. This is the
  // only reliable way to autoplay with sound on Chrome/Safari.
  React.useImperativeHandle(externalRef, () => ({
    playPitch: () => {
      gestureUnmuteRef.current = true;
      setActivePopup('pitch');
      setAutoplayPitch(true);
      const v = videoRef.current;
      if (!v) return false;
      v.muted = false;
      // Force the browser to (re)load the source if it was
      // deprioritized while off-screen — networkState=3 means no
      // source has been fetched yet.
      try { if (v.networkState === 3 || v.readyState === 0) v.load(); } catch (_e) {/* noop */}
      try { v.currentTime = 0; } catch (_e) {/* noop */}
      const p = v.play();
      if (p && typeof p.catch === 'function') {
        p.catch(() => {
          v.muted = true;
          const p2 = v.play();
          if (p2 && typeof p2.catch === 'function') p2.catch(() => {});
        });
      }
      return true;
    },
    pausePitch: () => {
      const v = videoRef.current;
      if (v) { try { v.pause(); } catch (_e) {/* noop */} }
    },
    openCaps: () => setActivePopup('caps'),
    // Opens whichever popup matches the LIVE admin pitch_state —
    // 'normal' → pitch + autoplay, 'inactive'/'disabled' → caps.
    // PERMISSION GATE: treats pitch_state as a hard permission. If
    // we haven't loaded the live setting yet, we DO NOT autoplay
    // pitch — that was causing the pitch video to flash even after
    // the admin disabled it. The fetch is awaited and the popup is
    // chosen from the AUTHORITATIVE value. This sacrifices unmuted
    // autoplay in the (rare) case where the fetch hadn't completed
    // before the click, but that's the correct tradeoff: muted
    // pitch can be unmuted by the recipient with a tap, but an
    // incorrectly-shown pitch (when admin disabled it) is a
    // policy violation that can't be undone.
    openLearnAboutOrb: async () => {
      let live = pitchState;
      // If we haven't confirmed the live state yet, await the fetch
      // before choosing the popup. Don't trust the 'normal' default.
      if (!pitchStateLoaded) {
        try {
          const apiBase = process.env.REACT_APP_BACKEND_URL || '';
          const r = await fetch(`${apiBase}/api/business-cards/public-settings`);
          if (r.ok) {
            const j = await r.json();
            live = j.pitch_state || 'normal';
            setPitchState(live);
            setPitchStateLoaded(true);
          } else {
            // Couldn't load → safest fallback is CAPs (no policy
            // violation; user can still discover ORB content).
            live = 'inactive';
          }
        } catch (_e) {
          live = 'inactive';
        }
      }

      if (live === 'inactive' || live === 'disabled') {
        // Always-on path when pitch is admin-disabled: route directly
        // to the CAPs popup.
        setActivePopup('caps');
        return;
      }

      // 'normal' branch — open pitch + autoplay. Synchronous so the
      // gesture token survives for unmuted autoplay. (Note: if we
      // had to await above, the gesture is already lost; the video
      // will fall back to muted autoplay.)
      gestureUnmuteRef.current = true;
      setActivePopup('pitch');
      setAutoplayPitch(true);
      const v = videoRef.current;
      if (v) {
        v.muted = false;
        try { if (v.networkState === 3 || v.readyState === 0) v.load(); } catch (_e) {/* noop */}
        try { v.currentTime = 0; } catch (_e) {/* noop */}
        const p = v.play();
        if (p && typeof p.catch === 'function') {
          p.catch(() => {
            v.muted = true;
            const p2 = v.play();
            if (p2 && typeof p2.catch === 'function') p2.catch(() => {});
          });
        }
      }

      // Background refetch — if the admin flipped pitch_state
      // since the initial load, correct the popup choice WITHOUT
      // a reload (race-condition safety net).
      try {
        const apiBase = process.env.REACT_APP_BACKEND_URL || '';
        const r = await fetch(`${apiBase}/api/business-cards/public-settings`);
        if (!r.ok) return;
        const j = await r.json();
        const fresh = j.pitch_state || 'normal';
        if (fresh === live) return;
        setPitchState(fresh);
        if (fresh === 'inactive' || fresh === 'disabled') {
          if (v) { try { v.pause(); } catch (_e) {/* noop */} }
          setActivePopup('caps');
        }
      } catch (_e) {/* keep current branch */}
    },
    closePopup: () => setActivePopup(null),
  }), [pitchState]);

  // Measure profile picture's exact top position whenever pitch, waitlist, or caps opens
  useEffect(() => {
    if (!isVisible) return;
    if ((activePopup === 'pitch' || activePopup === 'waitlist' || activePopup === 'caps' || activePopup === 'coming-soon') && profilePicRef.current) {
      const rect = profilePicRef.current.getBoundingClientRect();
      setPitchTopPx(Math.round(rect.top) - 6);
    }
    if (activePopup === 'pitch') {
      setGradientOn(false); // always start fully black
      pitchPlayedTrackedRef.current = false;
      lastPitchPctRef.current = 0;
      // Only show the "Tap for sound" overlay when the pitch was
      // auto-opened (e.g. ?pitch=1 from an email link) and the
      // browser may have forced muted autoplay. When the user
      // clicked a button SYNCHRONOUSLY (gestureUnmuteRef=true),
      // unmuted autoplay is allowed so the overlay is suppressed
      // for the entire viewing — even if the browser silently fell
      // back to muted playback.
      if (gestureUnmuteRef.current) {
        setVideoMuted(false);
        setSuppressTapOverlay(true);
        gestureUnmuteRef.current = false; // consume — re-arm on next user gesture
      } else {
        setVideoMuted(true);
        setSuppressTapOverlay(false);
      }
    }
    // If we arrived via ?pitch=1 OR opened in-app, try to play the
    // video WITH audio. If the browser blocks autoplay-with-sound,
    // fall back to muted autoplay so the visuals still start rolling.
    if (activePopup === 'pitch' && autoplayPitch) {
      const t = setTimeout(() => {
        const v = videoRef.current;
        if (!v) return;
        v.muted = false;
        const p = v.play();
        if (p && typeof p.catch === 'function') {
          p.catch(() => {
            v.muted = true;
            const p2 = v.play();
            if (p2 && typeof p2.catch === 'function') p2.catch(() => {});
          });
        }
      }, 120);
      return () => clearTimeout(t);
    }
  }, [activePopup, isVisible]);

  // Typewriter effect for the selected CAP label (runs after the dissolve cascade)
  useEffect(() => {
    if (!selectedCap) {
      setTypedCap('');
      setTypedBody('');
      setShowExampleBtn(false);
      return;
    }
    if (skipTyping) {
      setTypedCap(selectedCap);
      return;
    }
    setTypedCap('');
    setTypedBody('');
    setShowExampleBtn(false);
    const DISSOLVE_MS = 1400; // header + 5 staggered rows
    const CHAR_MS = 80;
    let intervalId;
    const startId = setTimeout(() => {
      let i = 0;
      intervalId = setInterval(() => {
        i++;
        setTypedCap(selectedCap.slice(0, i));
        if (i >= selectedCap.length) clearInterval(intervalId);
      }, CHAR_MS);
    }, DISSOLVE_MS);
    return () => {
      clearTimeout(startId);
      if (intervalId) clearInterval(intervalId);
    };
  }, [selectedCap, skipTyping]);

  // Typewriter effect for the body paragraph (runs after the header typewriter completes)
  useEffect(() => {
    if (!selectedCap) return;
    const content = capContent[selectedCap];
    if (!content) return;
    const body = content.body;
    if (skipTyping) {
      setTypedBody(body);
      const btnT = setTimeout(() => setShowExampleBtn(true), 350);
      return () => clearTimeout(btnT);
    }
    // Start after: dissolve(1400) + header typing (label.length × 80) + small gap
    const bodyStartDelay = 1400 + selectedCap.length * 80 + 350;
    const BASE_MS = 32;
    const delayForChar = (ch) => {
      if (ch === '.' || ch === '!' || ch === '?') return 700;
      if (ch === ',' || ch === ';' || ch === ':') return 130;
      if (ch === '\n') return 300;
      return BASE_MS;
    };
    let timers = [];
    let cancelled = false;
    const startId = setTimeout(() => {
      let i = 0;
      const step = () => {
        if (cancelled) return;
        i++;
        setTypedBody(body.slice(0, i));
        if (i >= body.length) {
          const btnT = setTimeout(() => { if (!cancelled) setShowExampleBtn(true); }, 300);
          timers.push(btnT);
          return;
        }
        const nextCh = body.charAt(i - 1); // pause after the char just typed
        const t = setTimeout(step, delayForChar(nextCh));
        timers.push(t);
      };
      step();
    }, bodyStartDelay);
    timers.push(startId);
    return () => {
      cancelled = true;
      timers.forEach(clearTimeout);
    };
  }, [selectedCap, skipTyping]);

  // Example view typewriter (triggered after See Example is clicked)
  useEffect(() => {
    if (!exampleActive) {
      setTypedExHeader('');
      setTypedExBody('');
      setShowExCTAs(false);
      return;
    }
    const content = selectedCap && capContent[selectedCap]?.example;
    if (!content) return;
    if (skipTyping) {
      setTypedExHeader(content.header);
      setTypedExBody(content.body);
      const btnT = setTimeout(() => setShowExCTAs(true), 400);
      return () => clearTimeout(btnT);
    }
    setTypedExHeader('');
    setTypedExBody('');
    setShowExCTAs(false);
    const HEADER_CHAR_MS = 80;
    const BODY_BASE_MS = 32;
    const delayForChar = (ch) => {
      if (ch === '.' || ch === '!' || ch === '?') return 700;
      if (ch === ',' || ch === ';' || ch === ':') return 130;
      if (ch === '\n') return 300;
      return BODY_BASE_MS;
    };
    let timers = [];
    let cancelled = false;
    // Start typing header after a tiny mount delay
    const startHeader = setTimeout(() => {
      let i = 0;
      const h = content.header;
      const hStep = () => {
        if (cancelled) return;
        i++;
        setTypedExHeader(h.slice(0, i));
        if (i >= h.length) {
          const startBody = setTimeout(() => {
            let j = 0;
            const b = content.body;
            const bStep = () => {
              if (cancelled) return;
              j++;
              setTypedExBody(b.slice(0, j));
              if (j >= b.length) {
                const btnT = setTimeout(() => { if (!cancelled) setShowExCTAs(true); }, 400);
                timers.push(btnT);
                return;
              }
              const ch = b.charAt(j - 1);
              const t = setTimeout(bStep, delayForChar(ch));
              timers.push(t);
            };
            bStep();
          }, 350);
          timers.push(startBody);
          return;
        }
        const t = setTimeout(hStep, HEADER_CHAR_MS);
        timers.push(t);
      };
      hStep();
    }, 60);
    timers.push(startHeader);
    return () => {
      cancelled = true;
      timers.forEach(clearTimeout);
    };
  }, [exampleActive, selectedCap, skipTyping]);

  // Show Skip 3 seconds after a description/example becomes active.
  // Reset whenever we leave the description/example or go back to grid.
  useEffect(() => {
    setShowSkipBtn(false);
    if (!selectedCap) return;
    if (exDissolving) return;
    const t = setTimeout(() => setShowSkipBtn(true), 3000);
    return () => clearTimeout(t);
  }, [selectedCap, exampleActive, exDissolving]);

  const togglePlay = () => {
    const v = videoRef.current; if (!v) return;
    if (v.paused) { v.play(); } else { v.pause(); }
  };
  const skip = (secs) => {
    const v = videoRef.current; if (!v) return;
    v.currentTime = Math.max(0, Math.min((v.duration || 0), v.currentTime + secs));
  };
  const cycleSpeed = () => {
    const speeds = [1, 1.25, 1.5, 2, 0.5];
    const idx = speeds.indexOf(playbackRate);
    const next = speeds[(idx + 1) % speeds.length];
    setPlaybackRate(next);
    if (videoRef.current) videoRef.current.playbackRate = next;
  };

  // Swipe-down-to-close for any popup
  const swipeRef = useRef(null);
  const findScrollableAncestor = (start, boundary) => {
    let el = start;
    while (el && el !== boundary) {
      const style = window.getComputedStyle(el);
      const oy = style.overflowY;
      if ((oy === 'auto' || oy === 'scroll') && el.scrollHeight > el.clientHeight) return el;
      el = el.parentElement;
    }
    return null;
  };
  const onPopupTouchStart = (e) => {
    const scrollable = findScrollableAncestor(e.target, e.currentTarget);
    swipeRef.current = {
      y: e.touches[0].clientY,
      scrollable,
      scrollTopAtStart: scrollable ? scrollable.scrollTop : 0
    };
  };
  const onPopupTouchEnd = (e) => {
    const s = swipeRef.current;
    swipeRef.current = null;
    if (!s) return;
    const endY = e.changedTouches[0].clientY;
    const dy = endY - s.y;
    // Close only on a decisive downward swipe AND only if the inner content wasn't scrolled
    const atTop = !s.scrollable || s.scrollTopAtStart === 0;
    if (dy > 70 && atTop) closePopup();
  };

  const name = cardData?.name || 'Unknown';
  const title = cardData?.title || cardData?.role || '';
  // The public /api/card/{user_id} endpoint already swaps phone/email to the
  // user's ORB-issued ones. Personal contact info is intentionally NOT
  // exposed on a business card. If ORB phone/email aren't yet set up for the
  // user's state, the corresponding action button is disabled below.
  const orbEmail = cardData?.email || '';
  const orbPhone = cardData?.phone || '';
  const profilePicture = cardData?.profile_picture || '';
  const hasOrbPhone = !!orbPhone;
  const hasOrbEmail = !!orbEmail;

  // iter175ci — Inject per-user OpenGraph + Twitter Card meta tags
  // into the document head as soon as we know whose card this is.
  // This is what makes the shared URL render a rich preview with
  // the right name + photo in JS-aware crawlers (Facebook, Twitter,
  // LinkedIn, Discord, Slack — all execute JS for OG scraping as of
  // 2025/2026). Apple LinkPresentation (iMessage) still doesn't
  // execute JS so its preview falls back to whatever's in the
  // static index.html (generic ORB Uplift branding).
  // We keep references to nodes we created so the cleanup function
  // removes them when the card unmounts and a different page takes
  // over the document head.
  useEffect(() => {
    if (!cardData) return undefined;
    const company = cardData.company || 'Collective Solutions Inc.';
    const ogTitle = name && name !== 'Unknown' ? name : 'ORB Uplift';
    const descParts = [title, company].filter(Boolean);
    const ogDesc = descParts.length ? descParts.join(' · ') : `Connect with ${ogTitle} on ORB Uplift.`;
    // og:image needs an absolute URL. We prefer the user's profile
    // photo (data URL) when present and small enough; otherwise fall
    // back to the favicon-180.png on the same origin.
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    let ogImage = `${origin}/favicon-180.png`;
    if (profilePicture && /^https?:\/\//i.test(profilePicture)) {
      ogImage = profilePicture;
    }
    const tags = [
      { property: 'og:type', content: 'profile' },
      { property: 'og:title', content: ogTitle },
      { property: 'og:description', content: ogDesc },
      { property: 'og:image', content: ogImage },
      { property: 'og:image:alt', content: ogTitle },
      { property: 'og:url', content: typeof window !== 'undefined' ? window.location.href : '' },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: ogTitle },
      { name: 'twitter:description', content: ogDesc },
      { name: 'twitter:image', content: ogImage },
      { name: 'twitter:image:alt', content: ogTitle },
    ];
    const created = [];
    // Original document title — we restore it on unmount so other
    // pages aren't stuck with the card subject's name.
    const originalTitle = document.title;
    document.title = `${ogTitle} — ORB Uplift`;
    tags.forEach((t) => {
      const selector = t.property
        ? `meta[property="${t.property}"]`
        : `meta[name="${t.name}"]`;
      let el = document.head.querySelector(selector);
      let wasExisting = !!el;
      if (!el) {
        el = document.createElement('meta');
        if (t.property) el.setAttribute('property', t.property);
        if (t.name) el.setAttribute('name', t.name);
        document.head.appendChild(el);
      }
      const previousContent = wasExisting ? el.getAttribute('content') : null;
      el.setAttribute('content', t.content);
      created.push({ el, wasExisting, previousContent });
    });
    return () => {
      document.title = originalTitle;
      created.forEach(({ el, wasExisting, previousContent }) => {
        if (wasExisting) {
          if (previousContent !== null) el.setAttribute('content', previousContent);
          else el.removeAttribute('content');
        } else {
          try { el.remove(); } catch (_) {/**/}
        }
      });
    };
  }, [cardData, name, title, profilePicture]);

  const getInitials = () => {
    if (!name || name === 'Unknown') return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const handleContactAction = (action) => {
    setContactAction(action);
    // iter175bl — If the visitor already filled the gate during this
    // session, fire the action directly with no popup.
    if (capturedContact) {
      void performContactAction(action, capturedContact);
      return;
    }
    setActivePopup('contact');
    setContactForm({ first_name: '', last_name: '', email: '', phone: '', notes: '', sms_consent: false });
  };

  // iter175bl + Save-Contact enrichment.
  //
  // Executes the post-gate action (email/call/text/save vcf) using
  // the visitor's captured contact details. Lead is recorded on the
  // server for EVERY action (even bypass ones) so analytics stay
  // accurate. The vcf now includes the app user's ORB phone, ORB
  // email, ORB address and profile photo (base64-embedded when we
  // can fetch the image, URL-referenced as a fallback).
  const performContactAction = async (action, formData) => {
    // Fire-and-forget lead record so the card owner sees who tapped
    // what. Captured form values are reused; only `action` changes.
    try {
      fetch(`${API_URL}/api/card/lead`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ card_owner_id: cardData?.id || '', ...formData, action })
      });
    } catch (_e) { /* non-blocking */ }
    try { track('contact_submit', { event_subtype: action, bypass: 'session' }); } catch (_) {}

    switch (action) {
      case 'email':
        if (!hasOrbEmail) {
          toast.error("This contact doesn't have an ORB email assigned yet.");
          return;
        }
        try { navigator.sendBeacon?.(`${API_URL}/api/c/click/${cardData?.id || ''}?kind=email&src=business_card&redirect=false`); } catch (_) {}
        try { track('outbound_click', { outbound_url: orbEmail, outbound_scheme: 'mailto' }); } catch (_) {}
        window.location.href = `mailto:${orbEmail}`;
        return;
      case 'call':
        if (!hasOrbPhone) {
          toast.error("This contact doesn't have an ORB phone assigned yet — your details are saved and they'll reach out once routing is enabled.");
          return;
        }
        try { navigator.sendBeacon?.(`${API_URL}/api/c/click/${cardData?.id || ''}?kind=call&src=business_card&redirect=false`); } catch (_) {}
        try { track('outbound_click', { outbound_url: orbPhone, outbound_scheme: 'tel' }); } catch (_) {}
        window.location.href = `tel:${orbPhone}`;
        return;
      case 'text':
        if (!hasOrbPhone) {
          toast.error("This contact doesn't have an ORB phone assigned yet — your details are saved and they'll reach out once routing is enabled.");
          return;
        }
        try { navigator.sendBeacon?.(`${API_URL}/api/c/click/${cardData?.id || ''}?kind=sms&src=business_card&redirect=false`); } catch (_) {}
        try { track('outbound_click', { outbound_url: orbPhone, outbound_scheme: 'sms' }); } catch (_) {}
        window.location.href = `sms:${orbPhone}`;
        return;
      case 'contact': {
        // Build a feature-rich vCard 3.0 string including ORB phone,
        // ORB email, ORB address and the user's profile photo if
        // present. Photo is base64-embedded when we can fetch it (most
        // reliable in iOS Contacts), otherwise we fall through with no
        // PHOTO line (URL-only PHOTO is widely unreliable on iOS).
        const orbAddress = (cardData?.orb_address || '').trim();
        const company = cardData?.company || 'Collective Solutions Inc.';
        let photoLine = '';
        if (profilePicture) {
          try {
            // Profile pictures come back from /api/card as data URLs
            // most of the time, but some legacy users still have
            // remote https URLs. fetch() handles both transparently.
            let b64 = '';
            let type = 'JPEG';
            const dataUrlMatch = profilePicture.match(/^data:(image\/[a-zA-Z+]+);base64,(.+)$/);
            if (dataUrlMatch) {
              type = dataUrlMatch[1].includes('png') ? 'PNG' : 'JPEG';
              b64 = dataUrlMatch[2];
            } else {
              const r = await fetch(profilePicture, { mode: 'cors' });
              if (r.ok) {
                const blob = await r.blob();
                const mime = blob.type || 'image/jpeg';
                const buf = await blob.arrayBuffer();
                const bytes = new Uint8Array(buf);
                let binary = '';
                for (let i = 0; i < bytes.byteLength; i += 1) binary += String.fromCharCode(bytes[i]);
                b64 = btoa(binary);
                type = mime.includes('png') ? 'PNG' : 'JPEG';
              }
            }
            if (b64) {
              const wrapped = b64.match(/.{1,75}/g).join('\n ');
              photoLine = `PHOTO;ENCODING=b;TYPE=${type}:${wrapped}\n`;
            }
          } catch (_e) { /* CORS / network — skip photo, vcard still valid */ }
        }
        // iter175bm — iOS Contacts shows the address under whatever
        // label the FIRST matching ADR line provides. We emit ONLY the
        // labeled `item1.ADR` variant so the address appears once,
        // tagged "ORB Office". Emitting both an unlabeled `ADR;TYPE=WORK`
        // and a labeled `item1.ADR` produced a duplicate entry on iOS.
        const adrLine = orbAddress
          ? `item1.ADR;TYPE=WORK:;;${orbAddress.replace(/,/g, '\\,').replace(/\n/g, ' ')};;;;\n`
            + `item1.X-ABLabel:ORB Office\n`
          : '';
        const vcard =
          `BEGIN:VCARD\n`
          + `VERSION:3.0\n`
          + `FN:${name}\n`
          + `N:${(cardData?.last_name || name.split(' ').slice(-1).join(' '))};${(cardData?.first_name || name.split(' ').slice(0, -1).join(' ') || name)};;;\n`
          + `TITLE:${title}\n`
          + `ORG:${company}\n`
          + (hasOrbEmail ? `EMAIL;TYPE=WORK:${orbEmail}\n` : '')
          + (hasOrbPhone ? `TEL;TYPE=WORK,VOICE:${orbPhone}\n` : '')
          + adrLine
          + photoLine
          + `END:VCARD`;
        const blob = new Blob([vcard], { type: 'text/vcard;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        // iter175bn — Attach the anchor to the DOM before clicking and
        // remove it after. The previous detached-anchor pattern caused
        // a structured-clone "object cannot be cloned" complaint from
        // the Emergent dev tooling Service Worker (it serializes
        // outbound click events for session replay and chokes on
        // anchors that have no parent node). Attached-anchor is the
        // standard, no-warning pattern.
        const a = document.createElement('a');
        a.href = url;
        a.download = `${name.replace(/\s+/g, '_')}.vcf`;
        a.rel = 'noopener';
        a.style.display = 'none';
        document.body.appendChild(a);
        a.click();
        // iter175cg — Distinguish "tapped Save Contact" (already
        // logged as button_click) from "actually saved the vCard
        // file". This event fires only when the .vcf blob is sent
        // to the OS download handler.
        try { track('vcf_downloaded'); } catch (_) {}
        // Defer cleanup so Safari / iOS Safari have time to start the
        // download before the blob URL is revoked.
        setTimeout(() => {
          try { a.remove(); } catch (_) {}
          try { URL.revokeObjectURL(url); } catch (_) {}
        }, 500);
        return;
      }
      default: return;
    }
  };

  const handleContactSubmit = async () => {
    if (!contactForm.first_name || !contactForm.last_name || !contactForm.email || !contactForm.phone) {
      toast.error('Please fill in all required fields');
      return;
    }
    setSubmitting(true);
    try {
      await fetch(`${API_URL}/api/card/lead`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ card_owner_id: cardData?.id || '', ...contactForm, action: contactAction })
      });
      track('contact_submit', { event_subtype: contactAction });
    } catch (e) { /* non-blocking */ }
    setSubmitting(false);
    setActivePopup(null);
    // iter175bl — Cache the captured contact for the rest of this
    // session so the other action buttons skip the gate.
    const captured = { ...contactForm };
    setCapturedContact(captured);
    await performContactAction(contactAction, captured);
  };

  const handleWaitlistSubmit = async () => {
    if (!waitlistForm.first_name || !waitlistForm.last_name || !waitlistForm.email) {
      toast.error('Please fill in your name and email');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/card/waitlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ card_owner_id: cardData?.id || '', ...waitlistForm })
      });
      if (!res.ok) throw new Error('bad');
      track('waitlist_submit');
      toast.success("You're on the waitlist!");
      setWaitlistForm({
        first_name: '', last_name: '', email: '', phone: '',
        address: '', social_handles: '', enter_drawing: false, opt_in_contact: false
      });
      setActivePopup(null);
    } catch (e) {
      toast.error('Could not submit. Please try again.');
    }
    setSubmitting(false);
  };

  const closePopup = () => {
    setActivePopup(null);
    setSelectedCap(null);
    setTypedCap('');
    setTypedBody('');
    setShowExampleBtn(false);
    setExDissolving(false);
    setExampleActive(false);
    setTypedExHeader('');
    setTypedExBody('');
    setShowExCTAs(false);
    setSkipTyping(false);
    if (videoRef.current) videoRef.current.pause();
    if (comingSoonRedirectId) {
      clearTimeout(comingSoonRedirectId);
      setComingSoonRedirectId(null);
    }
  };

  const resetToCapsGrid = () => {
    setSelectedCap(null);
    setTypedCap('');
    setTypedBody('');
    setShowExampleBtn(false);
    setExDissolving(false);
    setExampleActive(false);
    setTypedExHeader('');
    setTypedExBody('');
    setShowExCTAs(false);
    setSkipTyping(false);
  };

  const handleSeeExample = () => {
    track('example_read', { cap_title: selectedCap, cap_id: selectedCap });
    setSkipTyping(false); // reset for the example view
    setExDissolving(true);
    setTimeout(() => {
      setExDissolving(false);
      setExampleActive(true);
    }, 1400);
  };

  // Back button handler — stages a dissolve so the outgoing header fades cleanly
  const handleCapBack = () => {
    setExDissolving(true);
    setTimeout(() => {
      if (exampleActive) {
        setExampleActive(false);
        setTypedExHeader('');
        setTypedExBody('');
        setShowExCTAs(false);
        setSkipTyping(false);
      } else {
        // going from description → grid
        setSelectedCap(null);
        setTypedCap('');
        setTypedBody('');
        setShowExampleBtn(false);
        setSkipTyping(false);
      }
      setExDissolving(false);
    }, 600);
  };

  return (
    <>
      {/* Card */}
      <div
        className="w-[92vw] max-w-[420px] bg-white border-2 border-black rounded-3xl flex flex-col items-center relative overflow-hidden shadow-2xl"
        style={{
          // Always size to content. The previous fixed `height: 90vh`
          // with `overflow-hidden` clipped the bottom of the card on
          // smaller phones — quote was half-visible, social icons
          // missing entirely. Now every element below the avatar block
          // is guaranteed to be visible regardless of viewport height.
          fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'SF Pro Text', 'Helvetica Neue', Arial, sans-serif"
        }}
        data-testid="business-card-container"
      >
        <div
          className={`w-full flex flex-col items-center px-6 pt-[2vh] ${compact ? 'pb-4' : ''}`}
          style={{ paddingBottom: extraBottomPx ? `${extraBottomPx}px` : undefined }}
        >
          {/* Row 1: ORB Logo */}
          <img src={ORB_LOGO} alt="ORB" className="h-[11vh] object-contain mb-[1vh]" data-testid="card-orb-logo" />

          {/* Row 2: User Picture — object-top keeps foreheads visible */}
          <div className="mb-[0.8vh]" data-testid="card-user-picture" ref={profilePicRef}>
            <Avatar className="h-[22vh] w-[16vh] rounded-2xl border-2 border-gray-200">
              <AvatarImage src={profilePicture} className="object-cover object-top" style={{ aspectRatio: 'auto' }} />
              <AvatarFallback className="bg-gradient-to-br from-[#586D52] to-[#3a4d36] text-white text-6xl rounded-2xl">
                {getInitials()}
              </AvatarFallback>
            </Avatar>
          </div>

          {/* Row 3: Name */}
          <h1 className="text-2xl font-extrabold text-black leading-tight tracking-tight" data-testid="card-user-name">{name}</h1>

          {/* Row 4: Title (only if present) */}
          {title ? (
            <p className="text-gray-600 text-base capitalize font-medium" data-testid="card-user-title">{title}</p>
          ) : null}

          {/* Row 5: Company — reads cardData.company (server renders it as
               the state display_name for restricted-state users). */}
          <p className="text-gray-500 text-[11px] tracking-[0.2em] uppercase mb-[1.2vh] font-semibold" data-testid="card-company">
            {cardData?.company || 'Collective Solutions Inc.'}
          </p>

          {/* Divider — extra breathing room above so the line doesn't
               crowd the name/title/company block (kept tight so the
               action row + CTAs sit ~10px higher overall). */}
          <div className="w-full h-px bg-gray-200 mt-[0.55vh] mb-[1.2vh]" />

          {/* Action Icons — SMALLER */}
          <div className="flex items-center justify-center gap-4 mb-[1.2vh]">
            {[
              { icon: <Mail className="w-5 h-5" />, label: 'Email', action: () => { track('button_click', { event_subtype: 'email' }); handleContactAction('email'); } },
              { icon: <Phone className="w-5 h-5" />, label: 'Call', action: () => { track('button_click', { event_subtype: 'call' }); handleContactAction('call'); } },
              { icon: <MessageSquare className="w-5 h-5" />, label: 'Text', action: () => { track('button_click', { event_subtype: 'text' }); handleContactAction('text'); } },
              { icon: <UserPlus className="w-5 h-5" />, label: 'Contact', action: () => { track('button_click', { event_subtype: 'contact' }); handleContactAction('contact'); } }
            ].map((item) => (
              <button key={item.label} className="flex flex-col items-center gap-0.5" onClick={item.action} data-testid={`card-action-${item.label.toLowerCase()}`}>
                <div className="h-11 w-11 rounded-full bg-gray-100 border border-gray-200 flex items-center justify-center text-gray-700 hover:bg-gray-200 active:scale-95 transition-all">
                  {item.icon}
                </div>
                <span className="text-gray-600 text-[11px] font-medium">{item.label}</span>
              </button>
            ))}
          </div>

          {/* Divider */}
          <div className="w-full h-px bg-gray-200 mb-[1.2vh]" />

          {/* CTA Buttons */}
          <div className="w-full space-y-1.5 mb-[1.2vh]">
            <button className="w-full bg-white hover:bg-gray-50 border-2 border-[#586D52] text-[#586D52] text-sm font-bold py-2 rounded-lg transition-colors active:scale-[0.99]" onClick={() => { track('button_click', { event_subtype: 'waitlist' }); setActivePopup('waitlist'); }} data-testid="card-btn-waitlist">
              Join the Waitlist
            </button>
            <button className="w-full border border-gray-300 text-gray-800 hover:bg-gray-50 text-sm font-semibold py-2 rounded-lg transition-colors active:scale-[0.99]" onClick={() => { track('button_click', { event_subtype: 'caps' }); setActivePopup('caps'); }} data-testid="card-btn-caps">
              Learn about CAPs
            </button>
            {pitchState !== 'disabled' && (
              <button
                className="w-full border border-gray-300 text-gray-800 hover:bg-gray-50 text-sm font-semibold py-2 rounded-lg transition-colors active:scale-[0.99]"
                onClick={() => {
                  track('button_click', { event_subtype: 'pitch' });
                  if (pitchState === 'inactive') {
                    setActivePopup('coming-soon');
                    if (comingSoonRedirectId) clearTimeout(comingSoonRedirectId);
                    const id = setTimeout(() => {
                      setActivePopup('waitlist');
                      setComingSoonRedirectId(null);
                    }, 5000);
                    setComingSoonRedirectId(id);
                  } else {
                    startPitch();
                  }
                }}
                data-testid="card-btn-mission"
              >
                Watch the Pitch
              </button>
            )}
          </div>

          {/* Divider */}
          <div className="w-full h-px bg-gray-200 mb-[1.2vh]" />

          {/* Download ORB — appends user's referral_id as ?ref=… so
              ORB can attribute new signups back to this card. When no
              admin-provided URL is set yet, button is disabled. */}
          <button
            className={`w-full text-sm font-bold py-2 rounded-lg mb-[0.8vh] transition-colors active:scale-[0.99] ${downloadUrl ? 'bg-black hover:bg-gray-900 text-white' : 'bg-gray-300 text-gray-500 cursor-not-allowed'}`}
            disabled={!downloadUrl}
            onClick={() => {
              if (!downloadUrl) return;
              track('button_click', { event_subtype: 'download' });
              const ref = cardData?.referral_id;
              const sep = downloadUrl.includes('?') ? '&' : '?';
              const target = ref ? `${downloadUrl}${sep}ref=${encodeURIComponent(ref)}` : downloadUrl;
              // iter175cg — outbound_click captures the actual URL
              // (with referral) so admins can see which store /
              // destination the visitor was sent to.
              try {
                const scheme = (target.match(/^([a-z]+):/i) || [, 'https'])[1].toLowerCase();
                track('outbound_click', { outbound_url: target, outbound_scheme: scheme, event_subtype: 'download' });
              } catch (_) {}
              window.open(target, '_blank', 'noopener,noreferrer');
            }}
            data-testid="card-btn-download"
          >
            Download ORB
          </button>

          {/* Quote — SMALLER */}
          <p className="text-gray-400 text-[10px] italic text-center leading-relaxed mb-[0.8vh]">
            "The best way to predict the future is to create it"
          </p>

          {/* Social Icons */}
          <div className="flex items-center justify-center gap-3">
            {socialLinks.map((social) => (
              <a key={social.name} href={social.url} target="_blank" rel="noreferrer" className="h-7 w-7 rounded-full bg-gray-100 border border-gray-200 flex items-center justify-center text-gray-600 hover:text-black hover:bg-gray-200 transition-colors" data-testid={`card-social-${social.name.toLowerCase()}`}>
                <i className={`${social.icon} text-[10px]`} />
              </a>
            ))}
          </div>
        </div>
      </div>

      {/* ===== POPUPS ===== */}

      {activePopup && (
        <div className="fixed inset-0 z-[90] bg-black/50 backdrop-blur-sm" onClick={closePopup} />
      )}

      {/* Coming-Soon sheet — pitch_state==='inactive'. Same tall
          bottom-rising sheet styling as the waitlist/caps/pitch
          popups so the visual language stays consistent. */}
      {activePopup === 'coming-soon' && (
        <div
          className="fixed inset-x-0 md:inset-x-auto md:left-1/2 md:-translate-x-1/2 md:w-full md:max-w-[7.5in] bottom-0 z-[100] bg-gradient-to-b from-[#f6faf3] via-[#7f8d79] via-30% via-[#222] via-55% to-black rounded-t-3xl overflow-hidden flex flex-col shadow-[0_-20px_60px_rgba(0,0,0,0.35)] border-t border-white/40"
          style={{
            top: pitchTopPx != null ? `${pitchTopPx}px` : '19vh',
            fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'SF Pro Text', 'Helvetica Neue', Arial, sans-serif"
          }}
          data-testid="card-popup-coming-soon"
        >
          <div className="flex justify-center pt-3 pb-1 shrink-0">
            <div className="w-12 h-1.5 rounded-full bg-black/20" />
          </div>
          <div className="flex-1 flex flex-col items-center justify-center px-8 text-center">
            <div className="w-16 h-16 mb-6 rounded-full bg-[#586D52]/15 flex items-center justify-center">
              <Play className="w-7 h-7 text-[#586D52]" />
            </div>
            <h2 className="text-2xl font-extrabold text-gray-900 leading-tight">
              The Pitch video is coming soon!
            </h2>
            <p className="mt-3 text-base text-gray-800 leading-snug max-w-sm">
              Join our waitlist to stay updated
            </p>
            <p className="mt-8 text-xs uppercase tracking-[0.2em] text-white/80">
              Redirecting in 5 seconds…
            </p>
          </div>
        </div>
      )}

      {/* Waitlist Popup — temporarily wraps the public Wix form so the
          waitlist signup can keep moving while we rebuild the in-app
          flow. The bottom-sheet container is preserved (drag handle,
          rounded top corners, iOS spring) but the iframe fills the
          ENTIRE remaining popup area edge-to-edge. */}
      {activePopup === 'waitlist' && (
        <div
          className="fixed inset-x-0 md:inset-x-auto md:left-1/2 md:-translate-x-1/2 md:w-full md:max-w-[7.5in] bottom-0 z-[100] bg-black rounded-t-3xl overflow-hidden flex flex-col shadow-[0_-20px_60px_rgba(0,0,0,0.35)] border-t border-white/40"
          style={{
            top: pitchTopPx != null ? `${pitchTopPx}px` : '12vh',
            paddingBottom: 'env(safe-area-inset-bottom, 0px)',
            fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'SF Pro Text', 'Helvetica Neue', Arial, sans-serif"
          }}
          onTouchStart={onPopupTouchStart}
          onTouchEnd={onPopupTouchEnd}
          data-testid="popup-waitlist"
        >
          {/* Iframe fills the popup. Parent's padding-bottom reserves
              the iOS home-indicator strip and shows black through it
              (popup container is bg-black). */}
          <iframe
            title="Join the Waitlist"
            src="https://jah218.wixforms.com/f/7459137674209657991"
            className="w-full flex-1 border-0 block"
            allow="clipboard-write *; encrypted-media *; fullscreen *"
            referrerPolicy="no-referrer-when-downgrade"
            data-testid="waitlist-iframe"
          />

          {/* Floating drag handle — overlays the iframe so the popup
              still has its native bottom-sheet affordance without
              eating any vertical space. */}
          <div className="absolute top-0 left-0 right-0 flex justify-center pt-2 pointer-events-none z-10">
            <div className="w-12 h-1.5 rounded-full bg-black/30" />
          </div>
        </div>
      )}

      {/* CAPs Popup */}
      {activePopup === 'caps' && (
        <div
          className="fixed inset-x-0 md:inset-x-auto md:left-1/2 md:-translate-x-1/2 md:w-full md:max-w-[7.5in] bottom-0 z-[100] bg-gradient-to-b from-[#f6faf3] via-[#7f8d79] via-30% via-[#222] via-55% to-black rounded-t-3xl overflow-hidden flex flex-col shadow-[0_-20px_60px_rgba(0,0,0,0.35)] border-t border-white/40"
          style={{
            top: pitchTopPx != null ? `${pitchTopPx}px` : '18vh',
            fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'SF Pro Text', 'Helvetica Neue', Arial, sans-serif"
          }}
          onTouchStart={onPopupTouchStart}
          onTouchEnd={onPopupTouchEnd}
          data-testid="popup-caps"
        >
          <div className="flex justify-center pt-3 pb-1 shrink-0 relative">
            <div className="w-12 h-1.5 rounded-full bg-black/20" />
            {(selectedCap || exampleActive) && (
              <button
                onClick={handleCapBack}
                className="absolute left-4 top-2 h-9 w-9 rounded-full bg-white/80 backdrop-blur flex items-center justify-center text-gray-800 active:scale-95 shadow-sm"
                aria-label="Back"
                data-testid="cap-back-btn"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
            )}
          </div>

          <div className="shrink-0">
            {/* "Example" tiny label above the header (example view only) */}
            {!exDissolving && exampleActive && typedExHeader && (
              <p className="text-center text-gray-700 text-[11px] font-semibold uppercase tracking-[0.25em] mt-2">
                Example
              </p>
            )}

            <h2
              key={exDissolving ? 'dissolving' : exampleActive ? 'example-header' : 'description-header'}
              className={`cap-header-mount text-2xl font-extrabold text-center px-6 mb-4 text-gray-900 ${exampleActive ? 'mt-1' : 'mt-3'}`}
            >
              {exDissolving ? (
                '\u00A0'
              ) : exampleActive ? (
                typedExHeader ? (
                  <>
                    {typedExHeader}
                    {typedExHeader.length < (capContent[selectedCap]?.example?.header.length || 0) && (
                      <span className="inline-block ml-0.5 w-[2px] h-6 align-middle bg-gray-900 animate-pulse" />
                    )}
                  </>
                ) : '\u00A0'
              ) : selectedCap ? (
                typedCap ? (
                  <>
                    {typedCap}
                    {typedCap.length < selectedCap.length && (
                      <span className="inline-block ml-0.5 w-[2px] h-6 align-middle bg-gray-900 animate-pulse" />
                    )}
                  </>
                ) : '\u00A0'
              ) : (
                'Choose Your Cause'
              )}
            </h2>
          </div>

          <div className="flex-1 min-h-0 overflow-hidden px-5 pb-6 relative" style={{ paddingBottom: 'calc(1.5rem + env(safe-area-inset-bottom, 0px))' }}>
            {/* Grid (Choose Your Cause) */}
            {!exampleActive && (
              <div className="grid grid-cols-3 grid-rows-5 gap-2 h-full w-full">
                {capCategories.map((cat, idx) => {
                  const Icon = cat.icon;
                  const isSelected = selectedCap === cat.label;
                  const row = Math.floor(idx / 3);
                  // Header dissolves first (200ms). Grid rows then stagger after:
                  //   row 0 at 250ms, row 1 at 400ms, ..., row 4 at 850ms
                  const delay = 250 + row * 150;
                  return (
                    <button
                      key={cat.label}
                      onClick={() => { track('cap_view', { cap_title: cat.label, cap_id: cat.label }); setSelectedCap(cat.label); }}
                      className={`w-full h-full flex flex-col items-center justify-center gap-1 p-1.5 rounded-xl backdrop-blur border shadow-sm active:scale-95 focus:outline-none transition-opacity duration-[600ms] ease-out ${selectedCap ? 'opacity-0 pointer-events-none' : 'opacity-100'} ${
                        isSelected
                          ? 'bg-[#586D52] border-[#586D52] text-white'
                          : 'bg-white/70 border-white/60 text-gray-800'
                      }`}
                      style={{ transitionDelay: selectedCap ? `${delay}ms` : '0ms' }}
                      data-testid={`cap-${cat.label.toLowerCase().replace(/[^a-z]/g, '-')}`}
                    >
                      <Icon className={`w-5 h-5 shrink-0 ${isSelected ? 'text-white' : 'text-[#586D52]'}`} />
                      <span className={`text-[10px] font-semibold text-center leading-[1.1] ${isSelected ? 'text-white' : 'text-gray-800'}`}>{cat.label}</span>
                    </button>
                  );
                })}
              </div>
            )}

            {/* Description view (visible after selecting a CAP, before See Example) */}
            {!exampleActive && selectedCap && capContent[selectedCap] && (
              <div className="absolute inset-0 px-8 pt-2 flex flex-col" style={{ paddingBottom: 'calc(1.5rem + env(safe-area-inset-bottom, 0px))' }}>
                <p
                  className="flex-1 overflow-hidden text-[14px] leading-snug text-gray-900 whitespace-pre-line px-2 font-semibold transition-opacity duration-[600ms] ease-out"
                  style={{ opacity: exDissolving ? 0 : 1, transitionDelay: exDissolving ? '200ms' : '0ms' }}
                  data-testid="cap-body-text"
                >
                  {typedBody}
                  {typedBody && typedBody.length < capContent[selectedCap].body.length && (
                    <span className="inline-block ml-0.5 w-[2px] h-5 align-middle bg-gray-900 animate-pulse" />
                  )}
                </p>

                <div
                  className={`mt-4 transition-all duration-[700ms] ease-out ${
                    exDissolving
                      ? 'opacity-0 blur-md pointer-events-none'
                      : showExampleBtn
                        ? 'opacity-100 blur-0'
                        : 'opacity-0 blur-md pointer-events-none'
                  }`}
                  style={{ transitionDelay: exDissolving ? '400ms' : '0ms' }}
                >
                  <p className="text-center text-white text-sm font-semibold mb-2 drop-shadow">
                    Want to learn how?
                  </p>
                  <button
                    onClick={handleSeeExample}
                    className="w-full px-6 py-3 rounded-xl bg-white text-black text-base font-bold shadow-lg border border-black/10 active:scale-[0.98]"
                    data-testid="cap-see-example-btn"
                  >
                    See Example
                  </button>
                </div>
              </div>
            )}

            {/* Example view */}
            {exampleActive && selectedCap && capContent[selectedCap]?.example && (() => {
              const exBody = capContent[selectedCap].example.body;
              const splitMarker = 'This is where ORB comes in';
              const splitIdx = exBody.indexOf(splitMarker);
              const typedLen = typedExBody.length;
              const blackPart = splitIdx >= 0 ? typedExBody.slice(0, Math.min(typedLen, splitIdx)) : typedExBody;
              const whitePart = splitIdx >= 0 && typedLen > splitIdx ? typedExBody.slice(splitIdx) : '';
              const stillTyping = typedExBody && typedLen < exBody.length;
              const cursorInWhite = splitIdx >= 0 && typedLen >= splitIdx;
              return (
                <div className="absolute inset-0 px-8 pt-2 flex flex-col" style={{ paddingBottom: 'calc(1.5rem + env(safe-area-inset-bottom, 0px))' }}>
                  <p className="flex-1 overflow-hidden text-[14px] leading-snug whitespace-pre-line px-2 font-semibold" data-testid="cap-example-body">
                    <span className="text-gray-900">{blackPart}</span>
                    {whitePart && <span className="text-white">{whitePart}</span>}
                    {stillTyping && (
                      <span className={`inline-block ml-0.5 w-[2px] h-5 align-middle animate-pulse ${cursorInWhite ? 'bg-white' : 'bg-gray-900'}`} />
                    )}
                  </p>

                  <div
                    className={`mt-4 flex gap-3 transition-all duration-[700ms] ease-out ${
                      showExCTAs ? 'opacity-100 blur-0' : 'opacity-0 blur-md pointer-events-none'
                    }`}
                  >
                    <button
                      onClick={resetToCapsGrid}
                      className="flex-1 px-4 py-3 rounded-xl bg-white text-black text-base font-bold shadow-lg border border-black/10 active:scale-[0.98]"
                      data-testid="cap-learn-more-btn"
                    >
                      Learn More
                    </button>
                    <button
                      onClick={() => setActivePopup('waitlist')}
                      className="flex-1 px-4 py-3 rounded-xl bg-[#586D52] text-white text-base font-bold shadow-lg active:scale-[0.98]"
                      data-testid="cap-join-waitlist-btn"
                    >
                      Join Waitlist
                    </button>
                  </div>
                </div>
              );
            })()}
          </div>

          {/* Skip button — only while typewriter is running, hides before CTAs appear.
              Delayed 3s after typing starts so it doesn't pop in instantly. */}
          {(() => {
            if (exDissolving) return null;
            if (!selectedCap) return null;
            const inDescription = !exampleActive && capContent[selectedCap];
            const inExample = exampleActive && capContent[selectedCap]?.example;
            if (!inDescription && !inExample) return null;
            const descTyping = inDescription && typedBody.length < capContent[selectedCap].body.length;
            const exTyping = inExample && typedExBody.length < capContent[selectedCap].example.body.length;
            const skipVisible = (inDescription && descTyping) || (inExample && exTyping);
            if (!skipVisible) return null;
            if (!showSkipBtn) return null;
            return (
              <button
                onClick={() => setSkipTyping(true)}
                className="absolute left-1/2 -translate-x-1/2 bottom-3 z-[110] px-4 py-1.5 text-white text-sm font-semibold tracking-wide active:opacity-70 transition-opacity"
                style={{ bottom: 'calc(env(safe-area-inset-bottom, 0px) + 0.5rem)' }}
                data-testid="cap-skip-btn"
              >
                Skip
              </button>
            );
          })()}
        </div>
      )}

      {/* Pitch Popup — white bg, custom minimal controls */}
      {activePopup === 'pitch' && (
        <div
          className="fixed inset-x-0 md:inset-x-auto md:left-1/2 md:-translate-x-1/2 md:w-full md:max-w-[7.5in] bottom-0 z-[100] bg-black flex flex-col rounded-t-3xl overflow-hidden shadow-[0_-20px_60px_rgba(0,0,0,0.6)] border-t border-white/10"
          style={{
            top: pitchTopPx != null ? `${pitchTopPx}px` : '19vh',
            paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 1rem)',
            fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'SF Pro Text', 'Helvetica Neue', Arial, sans-serif"
          }}
          onClick={closePopup}
          onTouchStart={onPopupTouchStart}
          onTouchEnd={onPopupTouchEnd}
          data-testid="popup-pitch"
        >
          {/* Gradient fade-in overlay (triggered when video hits 25s) */}
          <div
            className="absolute inset-0 bg-gradient-to-b from-[#2a2a2a] via-[#111111] to-black pointer-events-none"
            style={{ opacity: gradientOn ? 1 : 0, transition: 'opacity 15000ms ease-out' }}
          />
          <div className="relative flex flex-col flex-1 min-h-0">
          <div className="flex justify-center pt-3 pb-2 shrink-0">
            <div className="w-12 h-1.5 rounded-full bg-white/30" />
          </div>

          <div className="flex-1 min-h-0 flex items-center justify-center relative" onClick={(e) => e.stopPropagation()}>
            {/* Video wrapper. On mobile, 77% of the viewport width.
                On desktop, cap the width so the full video fits the
                popup's vertical area (the popup is max 7.5in wide,
                and `max-h-full + aspect-video` keeps height in
                bounds of the available flex slot). */}
            <div
              className="relative w-[77%] md:w-auto md:max-w-full md:h-full md:max-h-[72vh] rounded-2xl overflow-hidden mt-[5px]"
              style={{ aspectRatio: undefined }}
            >
              <video
                ref={videoRef}
                src={PITCH_VIDEO}
                preload="auto"
                playsInline
                disablePictureInPicture
                controlsList="nodownload nofullscreen noremoteplayback noplaybackrate"
                onPlay={(e) => {
                  setIsPlaying(true);
                  if (!pitchPlayedTrackedRef.current) {
                    pitchPlayedTrackedRef.current = true;
                    track('pitch_played');
                  } else {
                    // iter175cg — Replay = a fresh `play` event after
                    // the video already finished. Captures viewers
                    // who restart the pitch to share / re-watch.
                    try {
                      const v = e.currentTarget;
                      const dur = v.duration || 0;
                      if (dur > 0 && v.currentTime < 1.5 && (lastPitchPctRef.current || 0) >= 95) {
                        lastPitchPctRef.current = 0;
                        track('pitch_replay');
                      }
                    } catch (_) {}
                  }
                }}
                onPause={(e) => {
                  setIsPlaying(false);
                  // iter175cg — Only count manual pauses, not the
                  // implicit pause that fires at video end (ended is
                  // handled separately).
                  try {
                    const v = e.currentTarget;
                    if (!v.ended && v.currentTime > 0.5 && v.duration > 0
                        && v.currentTime < v.duration - 0.5) {
                      track('pitch_pause', { pitch_pct: Math.round((v.currentTime / v.duration) * 100) });
                    }
                  } catch (_) {}
                }}
                onVolumeChange={(e) => setVideoMuted(!!e.currentTarget.muted)}
                onTimeUpdate={(e) => {
                  if (!gradientOn && e.currentTarget.currentTime >= 25) setGradientOn(true);
                  // Throttled pitch_progress event — fire at most
                  // once every ~10 seconds and on completion.
                  const dur = e.currentTarget.duration || 0;
                  if (dur > 0) {
                    const pct = (e.currentTarget.currentTime / dur) * 100;
                    const last = lastPitchPctRef.current;
                    if (pct - last >= 10 || pct >= 95) {
                      lastPitchPctRef.current = pct;
                      track('pitch_progress', { pitch_pct: Math.round(pct) });
                    }
                  }
                }}
                onEnded={() => { track('pitch_progress', { pitch_pct: 100 }); setActivePopup('waitlist'); }}
                onClick={(e) => { e.stopPropagation(); revealControls(); togglePlay(); }}
                className="block w-full h-auto md:w-auto md:h-full md:max-w-full md:object-contain cursor-pointer"
                data-testid="pitch-video"
              />

              {/* "Tap for sound" overlay — shown whenever the
                  video is muted (initial load, post-autoplay-fallback,
                  or any time the user manually mutes). One tap
                  unmutes inside a real click gesture, which Chrome,
                  Safari and iOS allow. The overlay also kicks off
                  play() so a video that failed to autoplay starts
                  immediately on tap. */}
              {videoMuted && !suppressTapOverlay && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    const v = videoRef.current;
                    if (!v) return;
                    v.muted = false;
                    try {
                      const p = v.play();
                      if (p && typeof p.catch === 'function') p.catch(() => {});
                    } catch (_e) { /* noop */ }
                  }}
                  className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-black/45 backdrop-blur-[1px] text-white animate-in fade-in duration-200"
                  aria-label="Tap for sound"
                  data-testid="pitch-tap-for-sound"
                >
                  <div className="w-20 h-20 rounded-full bg-white/95 text-black flex items-center justify-center shadow-2xl">
                    <Volume2 className="w-9 h-9" />
                  </div>
                  <span className="text-sm font-semibold tracking-wide drop-shadow-md">Tap for sound</span>
                </button>
              )}

              {/* Playback speed — top-left corner */}
              <button
                onClick={(e) => { e.stopPropagation(); revealControls(); cycleSpeed(); }}
                className={`absolute top-3 left-3 h-9 min-w-[42px] px-2.5 rounded-full bg-black/55 backdrop-blur-sm hover:bg-black/70 flex items-center justify-center text-white text-xs font-bold transition-opacity duration-200 ${showControls ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
                aria-label="Playback speed"
                data-testid="pitch-speed"
              >
                {playbackRate}x
              </button>

              {/* Playback controls — bottom center, fade in/out */}
              <div className={`absolute inset-x-0 bottom-3 flex items-center justify-center gap-5 px-4 transition-opacity duration-200 ${showControls ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
                <button
                  onClick={(e) => { e.stopPropagation(); revealControls(); skip(-10); }}
                  className="h-10 w-10 rounded-full bg-black/55 backdrop-blur-sm hover:bg-black/70 flex items-center justify-center text-white active:scale-95 transition-all"
                  aria-label="Rewind 10 seconds"
                  data-testid="pitch-rewind"
                >
                  <RotateCcw className="w-4 h-4" />
                </button>

                <button
                  onClick={(e) => { e.stopPropagation(); revealControls(); togglePlay(); }}
                  className="h-12 w-12 rounded-full bg-white/95 hover:bg-white flex items-center justify-center text-black active:scale-95 transition-all shadow-md"
                  aria-label={isPlaying ? 'Pause' : 'Play'}
                  data-testid="pitch-playpause"
                >
                  {isPlaying ? <Pause className="w-5 h-5" fill="currentColor" /> : <Play className="w-5 h-5 ml-0.5" fill="currentColor" />}
                </button>

                <button
                  onClick={(e) => { e.stopPropagation(); revealControls(); skip(10); }}
                  className="h-10 w-10 rounded-full bg-black/55 backdrop-blur-sm hover:bg-black/70 flex items-center justify-center text-white active:scale-95 transition-all"
                  aria-label="Forward 10 seconds"
                  data-testid="pitch-forward"
                >
                  <RotateCw className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
          </div>
        </div>
      )}

      {/* Contact Popup */}
      {activePopup === 'contact' && (
        <div
          className="fixed inset-x-0 md:inset-x-auto md:left-1/2 md:-translate-x-1/2 md:w-full md:max-w-[7.5in] bottom-0 z-[100] bg-gradient-to-b from-[#f6faf3] via-[#7f8d79] via-30% via-[#222] via-55% to-black rounded-t-3xl overflow-hidden flex flex-col shadow-[0_-20px_60px_rgba(0,0,0,0.35)] border-t border-white/40"
          style={{
            top: 'calc(42vh + 65px)',
            fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'SF Pro Text', 'Helvetica Neue', Arial, sans-serif"
          }}
          onTouchStart={onPopupTouchStart}
          onTouchEnd={onPopupTouchEnd}
          data-testid="popup-contact"
        >
          <div className="flex justify-center pt-3 pb-1">
            <div className="w-12 h-1.5 rounded-full bg-black/20" />
          </div>

          <div className="flex-1 overflow-y-auto px-6 pt-2 pb-6 space-y-3" style={{ paddingBottom: 'calc(1.5rem + env(safe-area-inset-bottom, 0px))' }}>
            <h3 className="text-lg font-bold text-center text-gray-900">Please provide your contact information</h3>

            <div className="flex gap-2">
              <Input placeholder="First Name *" value={contactForm.first_name} onChange={e => setContactForm(p => ({ ...p, first_name: e.target.value }))} className="text-sm h-11 bg-white border-gray-200 rounded-xl text-black placeholder:text-gray-400" data-testid="contact-first-name" />
              <Input placeholder="Last Name *" value={contactForm.last_name} onChange={e => setContactForm(p => ({ ...p, last_name: e.target.value }))} className="text-sm h-11 bg-white border-gray-200 rounded-xl text-black placeholder:text-gray-400" data-testid="contact-last-name" />
            </div>

            <div className="flex gap-2">
              <Input type="email" placeholder="Email *" value={contactForm.email} onChange={e => setContactForm(p => ({ ...p, email: e.target.value }))} className="text-sm h-11 bg-white border-gray-200 rounded-xl text-black placeholder:text-gray-400" data-testid="contact-email" />
              <Input type="tel" placeholder="Phone *" value={contactForm.phone} onChange={e => setContactForm(p => ({ ...p, phone: e.target.value }))} className="text-sm h-11 bg-white border-gray-200 rounded-xl text-black placeholder:text-gray-400" data-testid="contact-phone" />
            </div>

            <textarea
              placeholder="Notes (optional)"
              value={contactForm.notes}
              onChange={e => setContactForm(p => ({ ...p, notes: e.target.value }))}
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm resize-none h-16 bg-white text-black placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#586D52]/30"
              data-testid="contact-notes"
            />

            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={contactForm.sms_consent}
                onChange={e => setContactForm(p => ({ ...p, sms_consent: e.target.checked }))}
                className="mt-0.5 accent-[#586D52] h-4 w-4 shrink-0"
                data-testid="contact-sms-consent"
              />
              <span className="text-[11px] text-white leading-snug">
                By checking this box, you agree to receive sms messages from us. Message frequency varies & data rates may apply. Reply stop to opt out.{' '}
                <a
                  href="https://www.collectiveorb.com/privacy-policy"
                  target="_blank"
                  rel="noreferrer"
                  className="underline text-white font-medium"
                  data-testid="contact-privacy-policy-link"
                >
                  Privacy Policy
                </a>
                .
              </span>
            </label>

            <Button className="w-full bg-[#586D52] hover:bg-[#4a5d46] text-white text-base font-bold py-3 rounded-xl shadow-md" onClick={handleContactSubmit} disabled={submitting} data-testid="contact-submit-btn">
              {submitting ? 'Submitting...' : (() => {
                // iter175bh — CTA label reflects what the popup
                // will actually DO when submitted (call / text /
                // email / save contact) instead of a generic
                // "Submit". Falls back to "Submit" if no action
                // was set (defensive — handleContactAction always
                // sets one before opening the popup).
                const fn = (cardData?.first_name || (name || '').split(' ')[0] || '').trim();
                if (contactAction === 'call')  return `Call ${fn}`.trim() || 'Call';
                if (contactAction === 'text')  return `Text ${fn}`.trim() || 'Text';
                if (contactAction === 'email') return `Email ${fn}`.trim() || 'Email';
                if (contactAction === 'contact') return fn ? `Save ${fn}'s contact` : 'Save contact';
                return 'Submit';
              })()}
            </Button>
          </div>
        </div>
      )}
    </>
  );
});
BusinessCardContent.displayName = 'BusinessCardContent';

export default BusinessCardContent;
